import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, FileDown } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Shell, Empty } from "@/components/Shell";
import { RagBadge } from "@/components/Rag";
import { StatusPill } from "@/components/LogSection";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const Stat = ({ label, value, testId }) => (
  <div className="panel p-4" data-testid={testId}>
    <div className="field-label">{label}</div>
    <div className="mt-1 font-display text-2xl font-bold text-slate-900">{value}</div>
  </div>
);

const Grid = ({ title, headers, rows, testId, empty }) => (
  <section className="panel overflow-x-auto" data-testid={testId}>
    <div className="border-b border-slate-100 px-4 py-2.5 font-display text-sm font-semibold text-slate-800">{title}</div>
    {rows.length === 0 ? (
      <p className="px-4 py-6 text-center text-sm text-slate-400">{empty}</p>
    ) : (
      <table className="w-full min-w-[900px] text-sm">
        <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
          <tr>{headers.map((hd) => <th key={hd} className="px-4 py-2.5 font-semibold">{hd}</th>)}</tr>
        </thead>
        <tbody className="divide-y divide-slate-100">{rows}</tbody>
      </table>
    )}
  </section>
);

export default function ProgrammeReport() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api
      .get(`/programmes/${id}/status-report`)
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(apiError(e.response?.data?.detail)));
  }, [id]);

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/programmes/${id}/status-report.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `programme-report-${id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Programme report downloaded");
    } catch (e) {
      toast.error("Could not generate the programme report");
    }
  };

  if (!data) return <Shell testId="programme-report-loading"><Empty text="Building programme report…" /></Shell>;

  const s = data.rag_summary;
  return (
    <Shell
      testId="programme-report-page"
      title={`${data.programme.name} — Programme Status Report`}
      subtitle={`${data.programme.portfolio_name || "No portfolio"} · ${data.programme.manager || "No programme manager"} · generated ${data.generated_at?.slice(0, 10)}`}
      actions={
        <div className="flex items-center gap-2">
          <Button variant="outline" data-testid="programme-report-back-btn" onClick={() => navigate("/programmes")} className="gap-2">
            <ArrowLeft size={15} /> Back
          </Button>
          <Button data-testid="programme-report-pdf-btn" onClick={downloadPdf} className="gap-2" style={{ backgroundColor: "var(--brand-primary)" }}>
            <FileDown size={15} /> Download PDF
          </Button>
        </div>
      }
    >
      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-5">
        <Stat testId="pr-stat-projects" label="Projects" value={data.projects.length} />
        <Stat testId="pr-stat-avg" label="Avg % Complete" value={`${data.avg_percent_complete}%`} />
        <Stat testId="pr-stat-red" label="Red" value={s.Red} />
        <Stat testId="pr-stat-amber" label="Amber" value={s.Amber} />
        <Stat testId="pr-stat-green" label="Green" value={s.Green} />
      </div>

      {data.not_reported.length > 0 && (
        <div data-testid="pr-not-reported" className="mb-6 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          No current status update: {data.not_reported.join(", ")}
        </div>
      )}

      <div className="space-y-6">
        <Grid
          testId="pr-projects-grid"
          title="Project Health"
          headers={["Project", "Stage", "PM", "% Complete", "Overall", "Last Week", "Plan", "Resource", "Finance", "Benefits", "Vendor"]}
          empty="No active projects in this programme."
          rows={data.projects.map((p) => (
            <tr key={p.project_id} data-testid={`pr-project-row-${p.project_id}`} className="transition-colors hover:bg-slate-50">
              <td className="px-4 py-2.5">
                <button
                  data-testid={`pr-project-open-${p.project_id}`}
                  onClick={() => navigate(`/projects/${p.project_id}`)}
                  className="font-medium text-slate-800 underline-offset-2 hover:underline"
                >
                  {p.project_name}
                </button>
              </td>
              <td className="px-4 py-2.5 text-slate-600">{p.stage || "—"}</td>
              <td className="px-4 py-2.5 text-slate-600">{p.project_manager || "—"}</td>
              <td className="px-4 py-2.5 text-slate-600">{p.percent_complete || 0}%</td>
              <td className="px-4 py-2.5"><RagBadge value={p.overall_rag} /></td>
              <td className="px-4 py-2.5 text-slate-500">{p.previous_overall_rag || "—"}</td>
              {["plan_rag", "resource_rag", "finance_rag", "benefits_rag", "vendor_rag"].map((k) => (
                <td key={k} className="px-4 py-2.5">{p[k] ? <RagBadge value={p[k]} /> : "—"}</td>
              ))}
            </tr>
          ))}
        />

        <Grid
          testId="pr-milestones-grid"
          title={`Programme Milestones (${data.window[0]} to ${data.window[1]})`}
          headers={["Project", "Milestone", "Type", "Baseline", "Forecast", "RAG", "Status"]}
          empty="No milestones inside the reporting window."
          rows={data.milestones.map((m) => (
            <tr key={m.id} data-testid={`pr-milestone-row-${m.id}`}>
              <td className="px-4 py-2.5 text-slate-600">{m.project_name}</td>
              <td className="px-4 py-2.5 font-medium text-slate-800">{m.title}</td>
              <td className="px-4 py-2.5 text-slate-600">{m.milestone_type}</td>
              <td className="px-4 py-2.5 text-slate-600">{m.baseline_date || "—"}</td>
              <td className="px-4 py-2.5 text-slate-600">{m.forecast_date || "—"}</td>
              <td className="px-4 py-2.5"><RagBadge value={m.rag} /></td>
              <td className="px-4 py-2.5"><StatusPill value={m.status} /></td>
            </tr>
          ))}
        />

        <Grid
          testId="pr-risks-grid"
          title="Key Risks"
          headers={["Project", "Risk", "Owner", "Level", "RAG", "Status"]}
          empty="No risks flagged for reporting."
          rows={data.risks.map((r) => (
            <tr key={r.id} data-testid={`pr-risk-row-${r.id}`}>
              <td className="px-4 py-2.5 text-slate-600">{r.project_name}</td>
              <td className="px-4 py-2.5 font-medium text-slate-800">{r.title}</td>
              <td className="px-4 py-2.5 text-slate-600">{r.owner || "—"}</td>
              <td className="px-4 py-2.5 text-slate-600">{r.risk_level ?? "—"}</td>
              <td className="px-4 py-2.5"><RagBadge value={r.rag} /></td>
              <td className="px-4 py-2.5 text-slate-600">{r.status}</td>
            </tr>
          ))}
        />

        <Grid
          testId="pr-issues-grid"
          title="Key Issues"
          headers={["Project", "Issue", "Owner", "Priority", "RAG", "Status"]}
          empty="No issues flagged for reporting."
          rows={data.issues.map((i) => (
            <tr key={i.id} data-testid={`pr-issue-row-${i.id}`}>
              <td className="px-4 py-2.5 text-slate-600">{i.project_name}</td>
              <td className="px-4 py-2.5 font-medium text-slate-800">{i.title}</td>
              <td className="px-4 py-2.5 text-slate-600">{i.owner || "—"}</td>
              <td className="px-4 py-2.5 text-slate-600">{i.priority || "—"}</td>
              <td className="px-4 py-2.5"><RagBadge value={i.rag} /></td>
              <td className="px-4 py-2.5 text-slate-600">{i.status}</td>
            </tr>
          ))}
        />
      </div>
    </Shell>
  );
}
